import React, { useState } from 'react';

interface BoclawLogoProps {
  size?: number;
  showText?: boolean;
  className?: string;
  orgLogoUrl?: string | null;
  orgCode?: string | null;
  orgName?: string | null;
}

/**
 * BoclawLogo — renders organization logo or the default BoMate logo.
 * When `orgLogoUrl` is provided, renders an <img> with fallback to the default logo.
 * `orgCode` / `orgName` replace the default "BoCoder" / "博云 BOCLOUD" text.
 */
const BoclawLogo: React.FC<BoclawLogoProps> = ({
  size = 72,
  showText = false,
  className = '',
  orgLogoUrl,
  orgCode,
  orgName,
}) => {
  const [imgFailed, setImgFailed] = useState(false);

  const useOrgLogo = !!orgLogoUrl && !imgFailed;

  const logoNode = useOrgLogo ? (
    <img
      src={orgLogoUrl!}
      alt={orgCode || 'Logo'}
      style={{ height: size, width: size }}
      className="flex-shrink-0 object-contain rounded"
      onError={() => setImgFailed(true)}
    />
  ) : (
    <DefaultLogo size={size} />
  );

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Nudge the mark up a touch when paired with text so it optically
          aligns with the two-line copy instead of sitting low. */}
      {showText ? <span className="flex -mt-1">{logoNode}</span> : logoNode}
      {showText && (
        <div className="flex flex-col min-w-0">
          <span className="text-base font-extrabold leading-tight tracking-tight text-claude-text dark:text-claude-darkText truncate">
            {orgCode || 'BoCoder'}
          </span>
          <span className="text-[9px] tracking-[2px] leading-tight text-claude-textSecondary dark:text-claude-darkTextSecondary truncate">
            {orgName || '博云 BOCLOUD'}
          </span>
        </div>
      )}
    </div>
  );
};

const DefaultLogo: React.FC<{ size: number }> = ({ size }) => (
  <img
    src="/bomate-logo.png"
    alt="BoMate"
    width={size}
    height={size}
    style={{ width: size, height: size }}
    className="flex-shrink-0 object-contain"
    draggable={false}
  />
);
export default BoclawLogo;
